<template>
  <validation-provider
    v-slot="validationContext"
    :name="formField.label"
    :rules="validationRules"
  >
    <b-form-group
      v-if="!formField.dependency || entity[formField.dependency]"
      :class="formField.required ? 'required-field' : ''"
      :state="getValidationState(validationContext)"
    >
      <slot name="label">
        <label :for="fieldId">{{ formField.label }}</label>

        <feather-icon
          v-if="formField.layoutPreview"
          icon="LayoutIcon"
          class="cursor-pointer ml-25"
          size="16"
          @click="toggleLayoutPreview(formField.layoutPreview)"
        />
      </slot>
      <input
        v-if="formField.type === 'hidden'"
        :id="fieldId"
        :name="fieldId"
        :value="entity[formField.id]"
        type="hidden"
      />
      <b-form-input
        v-else-if="
          formField.type === 'text' ||
          formField.type === 'email' ||
          formField.type === 'number'
        "
        :id="fieldId"
        v-model="entity[formField.id]"
        :name="fieldId"
        :type="formField.type"
        :autofocus="autofocus"
        :readonly="formField.readonly"
        :class="formField.readonly ? 'form-control-plaintext' : ''"
        :placeholder="
          formField.placeholderVal
            ? entity[formField.id + 'placeholder']
            : formField.label + ' ' + config.title.single
        "
        trim
        @update="sync()"
      />
      <quill-editor
        v-else-if="formField.type === 'quill'"
        :id="fieldId"
        v-model="entity[formField.id]"
        :options="quillOptions"
        class="mb-5 quill-editor"
        @change="sync()"
      />
      <v-select
        v-else-if="
          formField.type === 'select' || formField.type === 'selectmultiple'
        "
        v-model="entity[formField.id]"
        :input-id="fieldId"
        :multiple="formField.type === 'selectmultiple'"
        :close-on-select="formField.type === 'select'"
        :options="formField.options"
        :reduce="
          (val) =>
            formField.type === 'select' && !formField.object ? val.id : val
        "
        :append-to-body="appendToBody"
        label="title"
        filterable
        autocomplete="nope"
        @input="dropdownClick(formField.id)"
      />
      <flat-pickr
        v-else-if="
          formField.type === 'date' ||
          formField.type === 'datetime' ||
          formField.type === 'time'
        "
        :id="fieldId"
        v-model="entity[formField.id]"
        :config="datepickerOptions"
        class="form-control"
        @on-open="updateDatePickerConfig"
      />
      <b-form-textarea
        v-else-if="formField.type === 'textarea'"
        :id="fieldId"
        v-model="entity[formField.id]"
        :rows="formField.rows ? formField.rows : 2"
        :placeholder="formField.label + ' ' + config.title.single"
        trim
      />
      <b-form-checkbox
        v-else-if="formField.type === 'checkbox'"
        :id="fieldId"
        v-model="entity[formField.id]"
        :placeholder="formField.label + ' ' + config.title.single"
        :inline="formField.inline"
        switch
        @change="checkboxClick(formField.id)"
      />
      <b-form-checkbox
        v-for="option in formField.options"
        v-else-if="formField.type === 'checkboxgroup'"
        :key="option.name"
        v-model="entity[option.name]"
        :name="option.name"
      >
        {{ $t(option.name) }}
      </b-form-checkbox>
      <b-input-group v-else-if="formField.type === 'password'">
        <b-form-input
          :id="fieldId"
          v-model="entity[formField.id]"
          :type="passwordFieldType"
          :placeholder="formField.label"
          trim
        />
        <b-input-group-append is-text>
          <feather-icon
            :icon="passwordToggleIcon"
            class="cursor-pointer"
            @click="togglePasswordVisibility"
          />
        </b-input-group-append>
      </b-input-group>
      <b-input-group v-else-if="formField.type === 'pdf'">
        <div class="d-flex flex-wrap flex-column">
          <a
            v-if="entity[formField.id]"
            :href="entity[formField.id]"
            target="_blank"
          >
            {{ $t('Preview') }}
          </a>
          <div class="button-wrap">
            <b-button variant="primary" @click="$refs.pdfFile.click()">
              <input
                ref="pdfFile"
                :id="fieldId"
                type="file"
                accept="application/pdf"
                class="d-none"
                @input="handleFileChange($refs.pdfFile, formField)"
              />
              <span>
                {{ $t(`${entity[formField.id] ? 'Update' : 'Add'}`) }}
              </span>
            </b-button>
            <b-button
              v-if="entity[formField.id] && !formField.required"
              variant="outline-secondary"
              class="ml-1"
              @click="handleFileChange(null, formField)"
            >
              <span>{{ $t('Delete') }}</span>
            </b-button>
          </div>
        </div>
      </b-input-group>

      <div v-else-if="formField.type === 'cropupload'" class="media mt-25 mb-2">
        <div class="media-aside align-self-start">
          <crop-upload
            v-model="modalShow"
            :width="formField.width"
            :height="formField.height"
            :params="{
              entityId: entity.id ? entity.id : 0,
              field: formField.id,
            }"
            :headers="uploadHeaders"
            :url="uploadPath"
            :img-format="formField.imgFormat ? formField.imgFormat : 'jpg'"
            :lang-ext="{
              hint: $t('Click or drag the file here to upload'),
              loading: $t('Uploading') + '...',
              noSupported: $t(
                'Browser is not supported, please use IE10+ or other browsers'
              ),
              success: $t('Upload success'),
              fail: $t('Upload failed'),
              preview: $t('Preview'),
              btn: {
                off: $t('Cancel'),
                close: $t('Close'),
                back: $t('Back'),
                save: $t('Save'),
              },
              error: {
                onlyImg: $t('Image only'),
                outOfSize: $t('Image exceeds size limit') + ': ',
                lowestPx: $t('Image size is too low. Expected at least') + ': ',
              },
            }"
            field="file"
            lang-type="en"
            no-circle
            @crop-upload-success="cropUploadSuccess"
            @crop-upload-fail="cropUploadFail"
          />

          <img
            
            :class="[`dimensions-${formField.width}${formField.height}`]"
            :src="
              entity[formField.id]
                ? entity[formField.id]
                : imagePlaceholders[`${formField.width}${formField.height}`]
            " class="crop-image-preview"
          />
        </div>
        <div class="ml-2">
          <b-button variant="primary" block @click="toggleCropModal()">
            <span>{{ $t(`${entity[formField.id] ? 'Update' : 'Add'}`) }}</span>
          </b-button>
          <b-button
            v-if="entity[formField.id] && !formField.required"
            variant="outline-secondary"
            class="mt-1"
            block
            @click="handleImageChange(null, formField)"
          >
            <span>{{ $t('Delete') }}</span>
          </b-button>
        </div>
      </div>
      <b-media
        v-else-if="formField.type === 'image'"
        class="mt-25 mb-2 col-md-8"
      >
        <template #aside>
          <b-avatar
            v-if="formField.type === 'image'"
            :src="entity[formField.id]"
            :text="formField.id === 'avatar' ? avatarText(entity.name) : ''"
            :size="formField.size ? formField.size : 198"
            rounded
          >
            <feather-icon
              v-if="!entity[formField.id]"
              :icon="formField.icon ? formField.icon : 'ImageIcon'"
              size="110"
            />
          </b-avatar>
        </template>
        <h4 v-if="formField.id === 'avatar'" class="mb-1">
          {{ entity.name }}
        </h4>
        <div class="d-flex flex-wrap">
          <b-button
            v-if="formField.type === 'image'"
            variant="primary"
            block
            @click="$refs.inputFile.click()"
          >
            <input
              ref="inputFile"
              :id="fieldId"
              type="file"
              accept="image/*"
              class="d-none"
              @input="handleImageChange($refs.inputFile, formField)"
            />
            <span>{{ $t(`${entity[formField.id] ? 'Update' : 'Add'}`) }}</span>
          </b-button>
          <b-button
            v-if="entity[formField.id] && !formField.required"
            variant="outline-secondary"
            class="mt-1"
            block
            @click="handleImageChange(null, formField)"
          >
            <span>{{ $t('Delete') }}</span>
          </b-button>
        </div>
      </b-media>
      <template v-else-if="formField.type === 'gallery'">
        <img
          v-if="previewImage"
          :src="previewImage"
          class="uploader-preview-image"
          @click="$refs.gallery.preview"
        />

        <img
          v-else
          :src="imagePlaceholders[1920800]"
          class="uploader-preview-image"
        />

        <uploader
          ref="gallery"
          v-model="entity[formField.id]"
          :files="processGalleryFiles(formField.id)"
          :limit-prompt="uploaderLimitPrompt()"
          :auto-upload="false"
          :max-width="4096"
          class="gallery-uploader"
          limit="8"
          @on-change="onUploaderChange($event, formField)"
          @on-delete="onUploaderDelete"
          @on-select-preview-image="onUploaderChangePreviewImage"
        />
      </template>

      <b-form-invalid-feedback :state="getValidationState(validationContext)">
        {{ validationContext.errors[0] }}
      </b-form-invalid-feedback>
    </b-form-group>
  </validation-provider>
</template>

<script>
import {
  BMedia,
  BAvatar,
  BButton,
  BFormGroup,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BFormCheckbox,
  BFormTextarea,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import axios from '@/libs/axios'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { quillEditor } from 'vue-quill-editor'
import { ValidationProvider } from 'vee-validate'
import { formUtils } from '@core/mixins/ui/forms'
import formValidation from '@core/comp-functions/forms/form-validation'
import { avatarText } from '@core/utils/filter'
import { required, alphaNum } from '@validations'
import Uploader from '@/libs/vux-uploader-component/src'
import cropUpload from '@/libs/vue-image-crop-upload/upload.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BMedia,
    BAvatar,
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BFormCheckbox,
    BFormTextarea,
    flatPickr,
    BFormInvalidFeedback,
    vSelect,
    quillEditor,
    Uploader,
    cropUpload,
    // Form Validation
    ValidationProvider,
  },
  mixins: [formUtils],
  props: {
    module: {
      type: String,
      required: true,
    },
    entity: {
      type: Object,
      required: true,
    },
    formField: {
      type: Object,
      required: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
      required: false,
    },
    checkboxClick: {
      type: Function,
      default: () => {},
      required: false,
    },
    dropdownClick: {
      type: Function,
      default: () => {},
      required: false,
    },
    idSuffix: {
      type: String,
      default: '',
      required: false,
    },
    appendToBody: {
      type: Boolean,
      default: false,
      required: false,
    },
    sync: {
      type: Function,
      default: () => {},
      required: false,
    },
  },
  setup(props) {
    const { refFormObserver, getValidationState } = formValidation(
      props.clearEntityData
    )

    const datepickerOptions = {
      allowInput: true,
      time_24hr: true,
      enableTime: true,
      noCalendar: false,
      dateFormat: '',
      defaultDate: null,
      maxDate: null,
      minDate: null,
    }

    const initDatePicker = () => {
      if (props.formField.minDate) {
        datepickerOptions.minDate = props.formField.minDate
      } else if (props.formField.refMinDate) {
        datepickerOptions.minDate = props.entity[props.formField.refMinDate]
      }

      if (props.formField.maxDate) {
        datepickerOptions.maxDate = props.formField.maxDate
      } else if (props.formField.refMaxDate) {
        datepickerOptions.maxDate = props.entity[props.formField.refMaxDate]
      }

      switch (props.formField.type) {
        case 'date':
          datepickerOptions.dateFormat = 'Y-m-d'
          datepickerOptions.enableTime = false
          break
        case 'datetime':
          datepickerOptions.dateFormat = 'Y-m-d H:i'
          break
        case 'time':
          datepickerOptions.noCalendar = true
          datepickerOptions.dateFormat = 'H:i'
          datepickerOptions.defaultDate = null
          break
        default:
          break
      }
    }

    if (
      props.formField.type === 'date' ||
      props.formField.type === 'datetime' ||
      props.formField.type === 'time'
    ) {
      initDatePicker()
    }

    const updateDatePickerConfig = (selectedDates, dateString, instance) => {
      if (props.formField.refMinDate) {
        datepickerOptions.minDate = props.entity[props.formField.refMinDate]
        instance.set('minDate', props.entity[props.formField.refMinDate])
      } else if (props.formField.refMaxDate) {
        datepickerOptions.maxDate = props.entity[props.formField.refMaxDate]
        instance.set('maxDate', props.entity[props.formField.refMaxDate])
      }
    }

    return {
      refFormObserver,
      getValidationState,
      avatarText,
      datepickerOptions,
      updateDatePickerConfig,
    }
  },
  data() {
    return {
      required,
      alphaNum,
      modalShow: false,
      swatches: [
        ['#F64272', '#F6648B', '#F493A7', '#F891A6', '#FFCCD5'],
        ['#8b5aff', '#a27bff', '#b99cff', '#d0bdff', '#e8deff'],
        ['#51e5db', '#74ebe3', '#96f0ea', '#b9f5f1', '#dcfaf8'],
        ['#ffa51a', '#ffb748', '#ffc976', '#ffdba3', '#ffedd1'],
      ],
      previewImage: '',
    }
  },
  computed: {
    uploadHeaders() {
      return {
        // eslint-disable-next-line prefer-template
        Authorization: 'Bearer ' + localStorage.getItem('accessToken'),
      }
    },
    uploadPath() {
      // eslint-disable-next-line prefer-template
      return process.env.VUE_APP_API_URL + `${this.module}/file-upload`
      // if (this.formField.autoupload) {
      //   axios.put(`${this.module}/file-upload`, {
      //     entityId: this.entity.id,
      //     img,
      //     field: this.formField.id,
      //   })
      // }
    },

    imagePlaceholders() {
      return {
        309417: require('@/assets/images/placeholders/309x417.png'),
        366406: require('@/assets/images/placeholders/366x406.png'),
        535485: require('@/assets/images/placeholders/535x485.png'),
        648595: require('@/assets/images/placeholders/648x595.png'),
        648969: require('@/assets/images/placeholders/648x969.png'),
        675365: require('@/assets/images/placeholders/675x365.png'),
        750365: require('@/assets/images/placeholders/675x365.png'),
        12001200: require('@/assets/images/placeholders/535x485.png'),
        1440600: require('@/assets/images/placeholders/1440x600.png'),
        1920800: require('@/assets/images/placeholders/1920x800.png'),
      }
    },

    validationRules() {
      const rules = []

      if (this.formField.rules) {
        rules.push(this.formField.rules)
      }

      if (this.formField.required) {
        rules.push('required')
      }

      return rules.join('|')
    },

    config() {
      return this.$store.getters[`${this.module}/config`]
    },

    fieldId() {
      return this.formField.id + this.idSuffix
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },

    quillOptions() {
      const options = {
        theme: 'snow',
        placeholder: this.formField.placeholderVal
          ? this.entity[`${this.formField.id}placeholder`]
          : `${this.formField.label} ${this.config.title.single}`,
      }

      if (this.formField.limitedOptions) {
        const modules = {
          toolbar: [
            [{ header: '1' }],
            [{ size: [false, 'large'] }],
            ['bold', 'italic'],
          ],
        }

        options.modules = modules
      }

      return options
    },
  },
  mounted() {
    if (this.formField.type === 'gallery') {
      const gallery = this.entity[this.formField.id]
      if(gallery.length) {
        this.previewImage = gallery[gallery.length - 1].url
      }
    }
  },
  methods: {
    toggleCropModal() {
      this.modalShow = !this.modalShow
    },
    toggleLayoutPreview(layoutItem) {
      const [layout] = layoutItem.split('_')

      this.$root[layout] = {
        highlight: layoutItem,
        item: this.entity,
      }

      this.$root.$emit('bv::show::modal', `${layout}-layout-preview`)
    },
    uploaderLimitPrompt() {
      return function (limit) {
        // eslint-disable-next-line prefer-template
        return this.$t('No more gallery images allowed! (' + limit + ')')
      }
    },

    async handleImageChange(input, formField) {
      const file = input && input.files && input.files[0]
      const targetEntity = this.entity[formField.parent]
        ? this.entity[formField.parent]
        : this.entity

      if (file) {
        targetEntity[formField.id] = file
      } else {
        targetEntity[formField.id] = ''
      }
      this.executeAutoupload(targetEntity[formField.id])
      this.sync()
    },

    async handleFileChange(input, formField) {
      const file = input && input.files && input.files[0]
      const targetEntity = this.entity[formField.parent]
        ? this.entity[formField.parent]
        : this.entity

      if (file) {
        targetEntity[formField.id] = file
      } else {
        targetEntity[formField.id] = ''
      }
      this.executeAutoupload(targetEntity[formField.id], 'pdf')
      this.sync()
    },

    async onUploaderChange(e, field) {
      // An image was just deleted, return here
      if (e.deleted) return

      const { id, width, height } = field

      // Remove the element
      this.entity[id].splice(this.entity[id].length - 1, 1)

      // --- Get Image Size --- //
      const minWidth = 800;
      const minHeight = 800;
      const imageSize = await (async () => {
        const url = URL.createObjectURL(e.blob)
        const img = new Image()
        img.src = url
        await img.decode()
        img.remove()
        URL.revokeObjectURL(e.blob)

        return {
          width: img.width,
          height: img.height,
        }
      })()
      // --- Get Image Size --- //
      //alert(`Image width is: ${width}`);
      //if (width !== imageSize.width || height !== imageSize.height) {
        if (imageSize.width < minWidth || imageSize.height < minHeight) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Please upload an image of the right dimensions'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })

        return
      }

      this.executeAutoupload(e.blob, 'jpg', true)
      this.sync()
    },

    onUploaderChangePreviewImage(e, list) {
      if (!list.length) {
        this.previewImage = ''
      } else {
        this.previewImage = list[e].url
      }
    },

    onUploaderDelete(e, cb) {
      e.deleted = true
      this.executeGalleryImgDeletion(e.id)
      cb() // Remove the local image and close previewer
    },

    processGalleryFiles(key) {
      if (this.entity[key]) {
        this.entity[key].forEach(async (item, e) => {
          if (item.image) this.entity[key][e].url = item.image
        })
      }
    },
    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    },
    cropUploadSuccess(jsonData) {
      this.entity[jsonData.field] = jsonData.file
      this.sync()
    },
    cropUploadFail(status, field) {
      console.log('-------- upload fail --------')
      console.log(status)
      // eslint-disable-next-line prefer-template
      console.log('field: ' + field)
    },
    executeAutoupload(file, fileType, arrayField) {
      if (this.formField.autoupload) {
        if (file.name && file.name.length > 0) {
          const formData = new FormData()
          if (this.entity.id != null) {
            formData.append('entityId', this.entity.id)
          }
          formData.append('file', file)
          formData.append('field', this.formField.id)
          if (fileType) {
            formData.append('type', fileType)
          }
          axios
            .post(`${this.module}/file-upload`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            })
            .then((response) => {
              if (arrayField) {
                this.entity[this.formField.id].push({
                  image: response.data.file,
                })
                this.previewImage = response.data.file
              } else {
                this.entity[this.formField.id] = response.data.file
              }
              if (response.data.thumbnail) {
                this.entity.thumbnail = response.data.thumbnail
              }
              this.$forceUpdate()
            })
        } else {
          this.executeImgDeletion(this.entity.id, this.formField.id)
        }
      }
    },
    executeImgDeletion(id) {
      axios.delete(`${this.module}/img-delete/${id}`)
    },
    executeGalleryImgDeletion(id) {
      axios.delete(`${this.module}/gallery-delete/${id}`)
    },
  },
}
</script>

<style lang="scss" scoped>
.quill-editor {
  height: 150px;
}

.uploader-preview-image {
  display: block;
  max-width: 960px;
  max-height: 400px;
  width: 100%;
  height: auto;
  cursor: pointer;
}

.crop-image-preview {
  width: 100%;
  height: auto;
}

.dimensions-309417 {
  max-width: 309px;
  max-height: 417px;
}
.dimensions-366406 {
  max-width: 366px;
  max-height: 406px;
}
.dimensions-535485 {
  max-width: 535px;
  max-height: 485px;
}
.dimensions-648595 {
  max-width: 648px;
  max-height: 595px;
}
.dimensions-648969 {
  max-width: 648px;
  max-height: 969px;
}
.dimensions-1920800 {
  max-width: 960px;
  max-height: 400px;
}
</style>

<style lang="scss">
.vux-uploader.gallery-uploader {
  padding: 0;

  .vux-uploader_title {
    display: none;
  }

  ul.vux-uploader_files {
    margin: 0;
    padding: 0;
  }
}
</style>
